import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DatePickerModule } from 'primeng/datepicker';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import {
  FunnelPlatform,
  FunnelRecurring,
  FunnelSummary,
  FunnelUser,
} from '../../models/user-funnel';
import { UserFunnelService } from '../../services/user-funnel.service';
import { AdminPageHeaderComponent } from '../shared/admin-page-header/admin-page-header.component';

@Component({
  selector: 'app-user-funnel',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DatePickerModule,
    TableModule,
    ProgressSpinnerModule,
    AdminPageHeaderComponent,
  ],
  templateUrl: './user-funnel.component.html',
  styleUrls: ['./user-funnel.component.scss'],
})
export class UserFunnelComponent {
  readonly title = 'Funil de Usuários';
  readonly subtitle = 'Acompanhe a conversão de cadastro até batalha com filtros de plataforma e recorrência.';

  rows: FunnelUser[] = [];
  summary: FunnelSummary = {
    registration_count: 0,
    link_count: 0,
    coin_purchase_count: 0,
    battle_count: 0,
    conversions: {
      registration_to_link_percentage: 0,
      registration_to_coin_purchase_percentage: 0,
      registration_to_battle_percentage: 0,
    },
  };

  isLoading = false;
  errorMessage = '';

  totalItems = 0;
  page = 1;
  rowsPerPage = 10;

  search = '';
  platform: FunnelPlatform = 'ALL';
  recurring: FunnelRecurring = 'all';

  since: Date;
  until: Date;

  readonly platformOptions: { label: string; value: FunnelPlatform }[] = [
    { label: 'Todas', value: 'ALL' },
    { label: 'League of Legends', value: 'RIOT_GAMES' },
    { label: 'Steam', value: 'STEAM' },
  ];

  readonly recurringOptions: { label: string; value: FunnelRecurring }[] = [
    { label: 'Todas', value: 'all' },
    { label: 'Somente Recorrentes (2+)', value: 'only_recurring' },
    { label: 'Somente Não Recorrentes', value: 'only_non_recurring' },
  ];

  constructor(
    private userFunnelService: UserFunnelService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    const { since, until } = this.getDefaultRange();
    this.since = since;
    this.until = until;

    this.route.queryParams.subscribe((params) => {
      this.search = params['search'] || '';
      this.platform = (params['platform'] || 'ALL') as FunnelPlatform;
      this.recurring = (params['recurring'] || 'all') as FunnelRecurring;
      this.page = parseInt(params['page'] || '1', 10);
      this.rowsPerPage = parseInt(params['page_size'] || '10', 10);

      const since = params['since'] ? this.parseDate(params['since']) : this.getDefaultRange().since;
      const until = params['until'] ? this.parseDate(params['until']) : this.getDefaultRange().until;
      this.applyRange(since, until);

      this.fetchFunnel();
    });
  }

  onApplyFilters(): void {
    this.page = 1;
    this.updateQueryParams();
  }

  clearFilters(): void {
    const defaults = this.getDefaultRange();
    this.search = '';
    this.platform = 'ALL';
    this.recurring = 'all';
    this.page = 1;
    this.rowsPerPage = 10;
    this.applyRange(defaults.since, defaults.until);
    this.updateQueryParams();
  }

  onSinceChange(): void {
    const defaults = this.getDefaultRange();
    const since = this.since ? this.startOfDay(this.since) : defaults.since;
    const until = this.until ? this.startOfDay(this.until) : defaults.until;
    this.applyRange(since, until);
    this.page = 1;
    this.updateQueryParams();
  }

  onUntilChange(): void {
    const defaults = this.getDefaultRange();
    const since = this.since ? this.startOfDay(this.since) : defaults.since;
    const until = this.until ? this.startOfDay(this.until) : defaults.until;
    this.applyRange(since, until);
    this.page = 1;
    this.updateQueryParams();
  }

  onLazyLoad(event: TableLazyLoadEvent): void {
    const rows = event.rows ?? this.rowsPerPage;
    const first = event.first ?? 0;
    const page = Math.floor(first / rows) + 1;

    let changed = false;

    if (rows !== this.rowsPerPage) {
      this.rowsPerPage = rows;
      changed = true;
    }

    if (page !== this.page) {
      this.page = page;
      changed = true;
    }

    if (changed) {
      this.updateQueryParams();
    }
  }

  formatPercentage(value: number): string {
    return `${Number(value || 0).toFixed(2)}%`;
  }

  formatPlatforms(platforms: FunnelPlatform[]): string {
    if (!platforms?.length) {
      return '-';
    }

    const labels = platforms.map((platform) => {
      if (platform === 'RIOT_GAMES') {
        return 'LoL';
      }
      if (platform === 'STEAM') {
        return 'Steam';
      }
      return platform;
    });

    return labels.join(', ');
  }

  private fetchFunnel(): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.userFunnelService
      .getFunnel({
        since: this.formatDateForApi(this.since),
        until: this.formatDateForApi(this.until),
        platform: this.platform,
        recurring: this.recurring,
        search: this.search,
        page: this.page,
        page_size: this.rowsPerPage,
      })
      .subscribe({
        next: (response) => {
          this.rows = response.results || [];
          this.summary = response.summary || this.summary;
          this.totalItems = response.count || 0;
          this.isLoading = false;
        },
        error: () => {
          this.rows = [];
          this.totalItems = 0;
          this.errorMessage = 'Não foi possível carregar os dados do funil de usuários.';
          this.isLoading = false;
        },
      });
  }

  private updateQueryParams(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        search: this.search.trim() || null,
        platform: this.platform,
        recurring: this.recurring,
        since: this.formatDateForApi(this.since),
        until: this.formatDateForApi(this.until),
        page: this.page,
        page_size: this.rowsPerPage,
      },
    });
  }

  private getDefaultRange(): { since: Date; until: Date } {
    const until = this.startOfDay(new Date());
    const since = this.addDays(until, -29);
    return { since, until };
  }

  private applyRange(since: Date, until: Date): void {
    const normalizedSince = this.startOfDay(since);
    const normalizedUntil = this.startOfDay(until);

    if (normalizedSince <= normalizedUntil) {
      this.since = normalizedSince;
      this.until = normalizedUntil;
    } else {
      this.since = normalizedUntil;
      this.until = normalizedSince;
    }
  }

  private parseDate(value: string): Date {
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) {
      return this.startOfDay(new Date());
    }
    return new Date(year, month - 1, day);
  }

  private formatDateForApi(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private startOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return this.startOfDay(result);
  }
}
